import { getToken } from 'firebase/messaging';
import { doc, setDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { getMessagingInstance, db } from './firebase';

export const requestNotificationPermission = async (userId: string, showErrorAlert = false) => {
  try {
    console.log('Solicitando permisos para:', userId);
    
    // 1. PEDIR PERMISO INMEDIATAMENTE (Sin esperas para no perder el User Gesture)
    let requestResult = 'default';
    if (typeof Notification !== 'undefined') {
      try {
        requestResult = await Notification.requestPermission();
      } catch (e) {
        console.warn('Error pidiendo permiso directo:', e);
      }
    }

    const messaging = await getMessagingInstance();
    
    if (!messaging) {
      if (showErrorAlert) {
        alert('Tu navegador o dispositivo no soporta notificaciones push o requiere que instales la aplicación en la pantalla de inicio primero (en iOS).');
      }
      return false;
    }

    // DIAGNÓSTICO PROFUNDO
    const currentPerm = Notification.permission;
    let systemPerm: string = 'unknown';
    try {
      const status = await navigator.permissions.query({ name: 'notifications' });
      systemPerm = status.state;
    } catch (e) {}

    console.log('DIAGNÓSTICO:', { currentPerm, systemPerm, requestResult });

    
    if (requestResult !== 'granted') {
      console.warn('Permiso denegado o cerrado');
      return null;
    }

    // 2. SOLO SI DIO PERMISO, INTENTAMOS EL SERVICE WORKER
    console.log('Permiso concedido. Preparando registro...');
    
    if (!('serviceWorker' in navigator)) {
      console.warn('Navegador no soporta SW');
      return null;
    }

    // Espera más corta para el SW para no bloquear al usuario
    const swTimeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('SW_TIMEOUT')), 4000)
    );

    try {
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        swTimeout
      ]) as ServiceWorkerRegistration;

      if (!registration || !registration.pushManager) {
        console.warn('El Service Worker o pushManager no está disponible en este navegador/contexto.');
        return "PENDING_TOKEN";
      }

      const currentToken = await getToken(messaging, { 
        vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
        serviceWorkerRegistration: registration
      });
      
      if (currentToken) {
        console.log('Token obtenido con éxito');
        await setDoc(doc(db, 'users', userId), {
          fcmTokens: arrayUnion(currentToken),
          fcmToken: currentToken,
          notificationsEnabled: true,
          updatedAt: new Date()
        }, { merge: true });
        
        return currentToken;
      }
    } catch (swError) {
      console.warn('El SW tardó demasiado, pero el permiso ya está dado. Se activará en el próximo inicio.', swError);
    }

    // Si llegamos aquí sin token pero con permiso, devolvemos algo que indique "OK pero sin token aún"
    return "PENDING_TOKEN";
  } catch (error: any) {
    console.error('Error en requestNotificationPermission:', error);
    return null;
  }
};

export const disableNotifications = async (userId: string) => {
  try {
    // 1. Intentar poner el flag a false inmediatamente para mejorar la respuesta visual
    await setDoc(doc(db, 'users', userId), {
      notificationsEnabled: false,
      updatedAt: new Date()
    }, { merge: true });

    // 2. Intentar quitar el token si es posible
    try {
      const messaging = await getMessagingInstance();
      if (messaging) {
        const swTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('SW Timeout')), 2000)
        );
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          swTimeout
        ]) as ServiceWorkerRegistration;

        if (registration && registration.pushManager) {
          const currentToken = await getToken(messaging, { 
            vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
            serviceWorkerRegistration: registration
          });

          if (currentToken) {
            await setDoc(doc(db, 'users', userId), {
              fcmTokens: arrayRemove(currentToken),
            }, { merge: true });
          }
        } else {
          console.warn('El Service Worker o pushManager no está disponible al desactivar.');
        }
      }
    } catch (e) {
      console.warn('No se pudo quitar el token de la lista, pero el flag se desactivó:', e);
    }

    return true;
  } catch (error: any) {
    console.error('Error crítico al desactivar:', error);
    return false;
  }
};


export const notifyClubStaff = async (clubId: string, notificationData: any) => {
  try {
    const usersRef = collection(db, 'users');
    const staffQuery = query(usersRef, where('clubId', '==', clubId), where('role', 'in', ['admin', 'superadmin', 'co_admin', 'empleado', 'gestor_turnos']));
    const snap = await getDocs(staffQuery);
    
    // Add the main clubId (often it's the admin itself, but just in case it doesn't match the query)
    const allUserIds = new Set(snap.docs.map(d => d.id));
    allUserIds.add(clubId);
    
    const promises = Array.from(allUserIds).map(uid => 
      addDoc(collection(db, 'notifications'), { ...notificationData, userId: uid })
    );
    
    await Promise.all(promises);
  } catch (e) {
    console.error('Error in notifyClubStaff:', e);
  }
};
